import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { type IssuedValueMap, useIssuedData } from './useIssuedData';
import { type JourneyManager } from './useJourney';
import { useIsMobile } from './useUserAgent';
import { useIsEmbedded } from './useIsEmbedded';
import { useCancelConfirm } from './useCancelConfirm';
import { useTranslation } from '../i18n/i18n';
import { useParentMessaging } from './useParentMessaging';
import { useRequestedData } from './useRequestedData';
import { type RequestedDataGroup } from './useRequestedData';
import { type LayoutData } from './useLayoutContext';
import {
  type AppAction,
  type AppState,
  appReducer,
  getInitialState,
} from '../journey/appState';
import { redirectInit } from '../journey/redirectInit';
import { embeddedInit } from '../journey/embeddedInit';

// -- Hook --

export type AppFlow = {
  state: AppState;
  isMobile: boolean;
  isEmbedded: boolean;
  requestedDataGroups: RequestedDataGroup[] | undefined;
  requestedDataLoading: boolean;
  issuedValueMap: IssuedValueMap;
  layoutData: LayoutData;
  handleCancel: () => Promise<void>;
  dispatch: React.Dispatch<AppAction>;
  deliveryMode: 'redirect' | 'webhook';
  notifyParent: ReturnType<typeof useParentMessaging>['notify'];
};

export function useAppFlow(journey: JourneyManager): AppFlow {
  const { locale } = useTranslation();
  const isEmbedded = useIsEmbedded();
  const isMobile = useIsMobile();
  const parentMessaging = useParentMessaging(isEmbedded);

  const [state, dispatch] = useReducer(appReducer, undefined, getInitialState);

  // Let the journey display a generic error page when it can't redirect back to the client.
  // There is no client to return to in that case, so the error page omits the "Leave" action.
  useEffect(() => {
    journey.setErrorHandler(() =>
      dispatch({ action: 'INIT_ERROR', variant: 'techFail', canLeave: false }),
    );
  }, [journey]);

  // In embedded mode, surface init-level errors to the parent page. Chulak-level
  // errors settle through journey.reject() (which posts ronan:error itself).
  useEffect(() => {
    if (state.type === 'error' && isEmbedded && state.variant !== 'cancel') {
      parentMessaging.notify({ type: 'ronan:error', error: state.variant });
    }
  }, [state, isEmbedded, parentMessaging]);

  const selectedToken = 'selectedToken' in state ? state.selectedToken : null;
  const { groups: requestedDataGroups, loading: requestedDataLoading } =
    useRequestedData(selectedToken, locale);
  const issuedValueMap = useIssuedData(selectedToken);
  const baseCancelConfirm = useCancelConfirm();

  // In embedded mode there is no navigation on cancel, so after the user
  // confirms we transition to the abort page ourselves.
  const handleCancel = useCallback(async () => {
    await baseCancelConfirm();
    if (isEmbedded && journey.isRedirecting()) {
      dispatch({ action: 'INIT_ERROR', variant: 'cancel' });
    }
  }, [baseCancelConfirm, isEmbedded, journey]);

  useEffect(() => {
    return isEmbedded
      ? embeddedInit(dispatch, journey, parentMessaging)
      : redirectInit(dispatch, journey, isMobile);
  }, [
    isEmbedded,
    isMobile,
    journey,
    parentMessaging.initData,
    parentMessaging.handshakeError,
  ]);

  // Build layout context from current state
  const layoutData: LayoutData = useMemo(() => {
    if ('session' in state) {
      const { session } = state;
      const request = session.context.request;
      return {
        client: request.client,
        requestType: request.type,
        interactionConfig: session.interactionConfig,
        requestedDataGroups,
        requestedDataLoading,
        demo: session.interactionConfig.demo,
      };
    }
    return { requestedDataGroups, requestedDataLoading };
  }, [state, requestedDataGroups, requestedDataLoading]);

  // The delivery mode is a pure function of whether Ronan runs embedded.
  // It is known synchronously from the first render — there is no third state.
  const deliveryMode: 'redirect' | 'webhook' = isEmbedded
    ? 'webhook'
    : 'redirect';

  return {
    state,
    isMobile,
    isEmbedded,
    requestedDataGroups,
    requestedDataLoading,
    issuedValueMap,
    layoutData,
    handleCancel,
    dispatch,
    deliveryMode,
    notifyParent: parentMessaging.notify,
  };
}
